import React from 'react'
import styled from 'styled-components'
import {
  colors,
  spacing,
  body,
  typographyMixin,
  borderRadius,
  mediaQuery,
  borderWidth,
} from 'src/constants/marketing-ds'
import CTAButton from 'src/components/common/CTAButtonNew'

const ChannelDetailsSection = ({ lang, market, channels }) => {
  return (
    <StyledChannelDetailsSection>
      {channels.map((channel) => (
        <ChannelBlock key={channel.id} id={channel.id}>
          <ContentContainer>
            <LeftContent>
              <LogoArea>
                <ChannelLogo src={channel.logo} alt={channel.name} />
              </LogoArea>

              <PriceText>{channel.price}</PriceText>

              <DescriptionText>{channel.description}</DescriptionText>

              <CTAButtonContainerDesktop>
                <CTAButton link={channel.ctaLink} isOutlined>
                  {channel.ctaText}
                </CTAButton>
              </CTAButtonContainerDesktop>
            </LeftContent>

            <ImagesContainer>
              {channel.images.map((image, index) => (
                <ChannelImage
                  key={index}
                  src={image}
                  alt={`${channel.name} content ${index + 1}`}
                />
              ))}
            </ImagesContainer>
          </ContentContainer>

          <CTAButtonContainerMobile>
            <CTAButton link={channel.ctaLink} isOutlined>
              {channel.ctaText}
            </CTAButton>
          </CTAButtonContainerMobile>
        </ChannelBlock>
      ))}
    </StyledChannelDetailsSection>
  )
}

export default ChannelDetailsSection

const StyledChannelDetailsSection = styled.section`
  width: 100%;
  box-sizing: border-box;
  background-color: ${colors.slate[30]};
  padding: 0 19px;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${mediaQuery.tablet} {
    padding: 0 ${spacing.md};
  }

  ${mediaQuery.desktop} {
    padding: 0 ${spacing.xl};
  }
`

const ChannelBlock = styled.div`
  width: 100%;
  max-width: 1400px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  scroll-margin-top: 100px;
  border-top: ${borderWidth.xs} solid rgba(250, 251, 255, 0.1);
  padding-top: ${spacing.xl};
  padding-bottom: ${spacing.xl};

  ${mediaQuery.tablet} {
    padding-top: 61px;
    padding-bottom: 61px;
  }

  ${mediaQuery.desktop} {
    padding-top: 94px;
    padding-bottom: 94px;
  }

  &:first-child {
    border-top: none;
  }
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  align-items: stretch;
  width: 100%;

  ${mediaQuery.desktop} {
    flex-direction: row;
    align-items: center;
  }
`

const LeftContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  align-items: center;
  text-align: center;

  ${mediaQuery.desktop} {
    flex: 0 0 35%;
    align-items: flex-start;
    text-align: left;
  }
`

const LogoArea = styled.div`
  height: auto;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`

const ChannelLogo = styled.img`
  width: auto;
  height: auto;
  max-height: 90px;
  max-width: 170px;
  object-fit: contain;
  display: block;
`

const PriceText = styled.p`
  ${typographyMixin(body.lg)};
  color: ${colors.slate[160]};
  margin: 0;

  ${mediaQuery.tablet} {
    ${typographyMixin(body.xl)};
  }
`

const DescriptionText = styled.p`
  ${typographyMixin(body.md)};
  color: ${colors.white};
  margin: 0;
  line-height: 1.5;

  ${mediaQuery.tablet} {
    ${typographyMixin(body.lg)};
  }
`

const ImagesContainer = styled.div`
  display: flex;
  gap: 11px;
  width: 100%;

  ${mediaQuery.tablet} {
    gap: ${spacing.md};
  }

  ${mediaQuery.desktop} {
    flex: 1;
  }
`

const CTAButtonContainerDesktop = styled.div`
  display: none;

  ${mediaQuery.desktop} {
    display: block;
  }

  a {
    transition: all 0.2s ease;

    &:hover {
      background-color: ${colors.white};
      color: ${colors.slate[10]};
      transform: translateY(-2px);
    }
  }
`

const CTAButtonContainerMobile = styled.div`
  display: flex;
  justify-content: center;

  ${mediaQuery.desktop} {
    display: none;
  }

  a {
    transition: all 0.2s ease;

    &:hover {
      background-color: ${colors.white};
      color: ${colors.slate[10]};
      transform: translateY(-2px);
    }
  }
`

const ChannelImage = styled.img`
  flex: 1;
  object-fit: cover;
  border-radius: ${borderRadius.sm};
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
  min-width: 0;
`
